import React, { useEffect, useState } from 'react';

import { Spinner } from '@lightspeed/unified-components-default-consumer-theme/react';
import { T } from '@transifex/react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Page } from '@hospitality-consumer-facing/components';
import {
  COLLAPSED_WIDGET_HEIGHT,
  ORIGIN_TRUST_STATUS,
  WIDGET_MESSAGE,
} from '@hospitality-consumer-facing/lsk-reservation/shared';

import { useGetBusinessLocation } from '../../reservation/hooks';
import { ErrorCard } from '../../shared/components/error-card';
import { EmptyLayout } from '../../shared/components/layout/empty-layout';
import { RESTAURANT_STATUS } from '../../shared/types';
import {
  useWidgetCommunicationContext,
  WidgetCommunicationProvider,
} from '../providers/widget-communication.provider';

import { WidgetReservationContent } from './widget-reservation-content';

interface WidgetPageInnerProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

function WidgetPageInner({ isOpen, setIsOpen }: WidgetPageInnerProps) {
  const {
    query: { restaurantId },
  } = useRouter();

  const { sendMessage, originTrustStatus, isStandalone, connectionError, parentOrigin } =
    useWidgetCommunicationContext();

  const isHandshakeDone = isStandalone || originTrustStatus !== ORIGIN_TRUST_STATUS.PENDING;

  const { data: businessLocation, isLoading: isBusinessLocationLoading } = useGetBusinessLocation(
    restaurantId as string,
    {
      headers: parentOrigin ? { 'Embedder-Origin': parentOrigin } : undefined,
      enabled: isHandshakeDone,
    },
  );

  const onOpen = () => {
    const success = sendMessage({ type: WIDGET_MESSAGE.OPEN });

    if (success) setIsOpen(true);
  };

  const onClose = () => {
    const success = sendMessage({ type: WIDGET_MESSAGE.CLOSE });

    if (success) setIsOpen(false);
  };

  useEffect(() => {
    const sendHeight = () => {
      window.requestAnimationFrame(() => {
        const height = isOpen ? document.body.clientHeight : COLLAPSED_WIDGET_HEIGHT;

        sendMessage({
          type: WIDGET_MESSAGE.RESIZE,
          payload: {
            height,
            isOpen,
          },
        });
      });
    };

    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);

    sendHeight();

    return () => {
      observer.disconnect();
    };
  }, [isOpen, sendMessage]);

  useEffect(() => {
    if (businessLocation?.integrationConfig.status === RESTAURANT_STATUS.INACTIVE) {
      sendMessage({ type: WIDGET_MESSAGE.DESTROY });
    }
  }, [businessLocation, sendMessage]);

  if (businessLocation?.integrationConfig.status === RESTAURANT_STATUS.INACTIVE) return null;

  // Error states (only in iframe mode)
  if (!isStandalone) {
    if (originTrustStatus === ORIGIN_TRUST_STATUS.PENDING) {
      return (
        <EmptyLayout>
          <div className="flex w-full h-screen justify-center items-center">
            <Spinner />
          </div>
        </EmptyLayout>
      );
    }

    // Security warning for unauthorized origins
    if (originTrustStatus === ORIGIN_TRUST_STATUS.UNTRUSTED) {
      return (
        <EmptyLayout>
          <ErrorCard data-testid="widget-security-error">
            <T
              _str="This widget cannot be displayed on this website for security reasons."
              _context="Security error message for unauthorized widget embedding"
              _tags="widget,security"
              _key="Widget.Security.UnauthorizedOrigin.error"
            />
          </ErrorCard>
        </EmptyLayout>
      );
    }

    if (connectionError) {
      return (
        <EmptyLayout>
          <ErrorCard data-testid="widget-connection-error">
            <T
              _str="Unable to establish secure connection with parent page."
              _context="Connection error message for widget communication failure"
              _tags="widget,error"
              _key="Widget.Connection.ParentPage.error"
            />
          </ErrorCard>
        </EmptyLayout>
      );
    }
  }

  return (
    <Page
      title={
        <T
          _str="Reservation"
          _context="Page title for the reservation widget"
          _tags="widget"
          _key="Widget.Page.Title.title"
        />
      }
    >
      <Head>
        <style>{`
          html { scrollbar-width: none; }
          html::-webkit-scrollbar { display: none; }
          body { box-sizing: border-box; }
        `}</style>
      </Head>
      <div className="flex flex-col shadow-lg">
        {isBusinessLocationLoading && (
          <div className="flex w-full h-screen justify-center items-center">
            <Spinner />
          </div>
        )}
        {!isBusinessLocationLoading && businessLocation && (
          <WidgetReservationContent
            businessLocation={businessLocation}
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
          />
        )}
      </div>
    </Page>
  );
}

export function WidgetPageContent() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <WidgetCommunicationProvider
      onUnauthorizedOrigin={(origin) => {
        console.warn('[Widget] Unauthorized embedding attempt from:', origin);
      }}
      handlers={{
        [WIDGET_MESSAGE.OPEN]: () => setIsOpen(true),
        [WIDGET_MESSAGE.CLOSE]: () => setIsOpen(false),
      }}
    >
      <WidgetPageInner isOpen={isOpen} setIsOpen={setIsOpen} />
    </WidgetCommunicationProvider>
  );
}
